import { Close, Done } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Skeleton,
} from '@mui/material';
import { SubmitIcon } from '../../../../vectors/index';
import { FC, useContext, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { PLANS } from '@utils/pricing';
import { authContext } from '@staffedupusa/shared-components';
import { useUpdatePlan } from '@features/payments/hooks/useUpdatePlan';
import PlanSelectionModalForExpired from './PlanSelectionModalForExpired';
import AddPaymentMethodModal from '@features/enterprise/components/AddPaymentMethodModal';
import { useGetPaymantDetails } from '@features/payments/hooks/useGetPaymantDetails';

type ConfirmationUpdateBillingModalProps = {
  isOpen: string;
  hasPaymentMethod: boolean;
  isExpired?: boolean;
  plan?: number;
  planIds?: { company_id?: number; plan_id?: number; new_plan_id: number }[];
  expiredCompanyName?: { companyName?: string; companyId: number }[];
  companyId?: number;
  companyIds?: number[];
  companyName?: string | null;
  handleClose: () => void;
  handleSuccess?: () => void;
};

const ConfirmationUpdateBillingModal: FC<
  ConfirmationUpdateBillingModalProps
> = ({
  isOpen,
  companyName,
  expiredCompanyName,
  plan,
  isExpired,
  companyId,
  companyIds,
  planIds,
  handleClose,
  handleSuccess,
  hasPaymentMethod,
}) => {
  const navigate = useNavigate();
  const { authData } = useContext(authContext);
  const billingCycle = authData?.user?.billing_cycle;
  const [errorText, setErrorText] = useState('');
  const [addPaymentMethodModal, setAddPaymentMethodModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('4');
  const [showPlanSelectionModal, setPlanSelectionModal] = useState('');
  const { updatePaymentPlan, isUpdatingPaymentPlan } = useUpdatePlan({
    setErrorText,
  });
  const { paymentInfo, isPaymentInfoLoading } = useGetPaymantDetails({
    companyId: companyId ? companyId : companyIds?.length === 1 ? companyIds[0] : undefined,
  });

  const options = {
    onSuccess: (data: {
      errors: any; status: number; message: string 
}) => {
      if (data.status == 200) {
        setErrorText('');
        toast.success(data.message);
        handleClose();
        handleSuccess?.();
        setAddPaymentMethodModal(false)
      } else if (data.status === 400) {
        if (data.errors?.length > 0) {
          setErrorText('Selected company have no payment method, please add payment method');
        }
      }
    },
  };

  const handleSubmit = () => {
    if ((plan !== undefined || planIds?.length === 1) && isOpen === 'self paid' && (paymentInfo?.status === 400)) {
      setAddPaymentMethodModal(true);
      return;
    }
    if (planIds && planIds?.some((p) => p.plan_id === 0 || p.plan_id === 999)) {
      setPlanSelectionModal(isOpen);
      return;
    }
    if (!hasPaymentMethod) {
      return navigate('#Payment-Info');
    }

    updatePaymentPlan(
      {
        billing_cycle: billingCycle ?? undefined,
        upgrade_plan: isOpen.includes('enterprise') ? true : false,
        downgrade_plan: !isOpen.includes('enterprise') ? true : false,
        switch: isOpen.includes('paid') ? true : false,
        plan_ids: planIds
          ? planIds
          : isExpired
            ? [
                {
                  company_id: companyId,
                  plan_id: plan ?? 0,
                  new_plan_id: Number(selectedPlan),
                },
              ]
            : plan
              ? [
                  {
                    company_id: companyId,
                    plan_id: plan,
                    new_plan_id: plan,
                  },
                ]
              : [],
      },
      options,
    );
  };

  return (
    <>
      <Dialog
        open={Boolean(isOpen)}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <IconButton
            sx={{ position: 'absolute', top: 5, right: 5 }}
            onClick={handleClose}
          >
            <Close />
          </IconButton>
          <br />
          <SubmitIcon />
          <br />
          {isPaymentInfoLoading ? (
            <Skeleton
              variant="text"
              width={200}
              height={40}
              sx={{ marginTop: 2 }}
            />
          ):(
            <>
              <Typography mt={4} variant="h4">{(isOpen === 'self paid') && ((plan !== undefined || planIds?.length === 1) && (paymentInfo?.status === 400)) && 'To move company to location billing add payment method'}</Typography>
              <Typography lineHeight={isOpen === 'self paid' ? '1.2rem' : 'auto'} color={isOpen === 'self paid' ? 'gray' : 'inherit'} variant={isOpen === 'self paid' ? 'body1' : 'h4'} mt={4}>
                {(plan !== undefined || planIds?.length === 1) && (paymentInfo?.status === 400) && isOpen === 'self paid' ? (
                  'To move payment responsibility to this location, please add a payment method now. You can also ask the location to log in, go to Account Settings > Billing & Upgrades > Manage Billing, where they can add a payment method and update their billing plans to take over billing responsibility.'
                      ) : planIds && isOpen === 'self paid' && planIds?.length > 1 ? (
                  'To move payment responsibility for these locations, please add a payment method. You can also ask the location to log in, go to Account Settings > Billing & Upgrades > Manage Billing, where they can add a payment method and update their billing plans to take over billing responsibility.'
                ) : (
                  <>
                    Are you sure{' '}
                    {isOpen.includes('paid') ? (
                      <>
                        to move <br /> <b>{companyName ?? 'selected companies'}</b>{' '}
                        to
                      </>
                    ) : (
                      'to'
                    )}{' '}
                    {isOpen === 'self paid'
                      ? 'location billing?'
                      : isOpen === 'enterprise paid'
                        ? 'enterprise billing?'
                        : isOpen}{' '}
                    {!isOpen.includes('paid') && <b> {companyName}</b>}
                  </>
                )}
              </Typography>
            </>
          )}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            {isExpired && (
              <FormControl component="fieldset">
                <FormLabel>Select Plan</FormLabel>
                <RadioGroup
                  value={selectedPlan}
                  onChange={(e) => setSelectedPlan(e.target.value)}
                  row
                >
                  <FormControlLabel
                    value="4"
                    control={<Radio />}
                    label={PLANS.STARTER.NAME}
                  />
                  <FormControlLabel
                    value="1"
                    control={<Radio />}
                    label={PLANS.SMARTER.NAME}
                  />
                  <FormControlLabel
                    value="2"
                    control={<Radio />}
                    label={PLANS.PRO.NAME}
                  />
                </RadioGroup>
              </FormControl>
            )}
            {errorText && <Alert severity="error">{errorText}</Alert>}
            <LoadingButton
              variant="contained"
              startIcon={<Done />}
              onClick={handleSubmit}
              loading={isUpdatingPaymentPlan}
              disabled={isUpdatingPaymentPlan || isPaymentInfoLoading}
            >
              {(plan !== undefined || planIds?.length === 1) && (paymentInfo?.status === 400) && isOpen === 'self paid'
                ? 'Add Payment'
                : planIds && isOpen === 'self paid'
                  ? 'Proceed to Billing Transfer'
                  : 'Confirm'}
            </LoadingButton>
            <Button
              startIcon={<Close />}
              variant="outlined"
              color="secondary"
              onClick={handleClose}
              disabled={isUpdatingPaymentPlan}
            >
              Cancel
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
      {showPlanSelectionModal && planIds && (
        <PlanSelectionModalForExpired
          isOpen={showPlanSelectionModal}
          handleClose={() => setPlanSelectionModal('')}
          handleSuccess={handleSuccess}
          companyIds={companyIds}
          planIds={planIds}
          hasPaymentMethod={hasPaymentMethod}
          expiredCompanyName={expiredCompanyName}
        />
      )}
      {addPaymentMethodModal && (companyId || planIds?.length === 1) && (
        <AddPaymentMethodModal
          handleClose={() => setAddPaymentMethodModal(false)}
          companyId={companyId || planIds?.[0].company_id}
          handleSucess={() => {
            updatePaymentPlan(
              {
                billing_cycle: billingCycle ?? undefined,
                upgrade_plan: false,
                downgrade_plan: true,
                switch: true,
                plan_ids: planIds
                  ? planIds
                  : isExpired
                    ? [
                      {
                        company_id: companyId,
                        plan_id: plan ?? 0,
                        new_plan_id: Number(selectedPlan),
                      },
                    ]
                    : plan
                      ? [
                        {
                          company_id: companyId,
                          plan_id: plan,
                          new_plan_id: plan,
                        },
                      ]
                      : [],
              },
              options,
            );
          }}
        />
      )}
    </>
  );
};

export default ConfirmationUpdateBillingModal;
